import React, { FC } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { EmployeeId } from '@modernloop/shared/ids';
import { Avatar, Chip, Skeleton, Stack } from '@mui/material';

import { DynamicAudience, useSmartEmployeeSelectLabelEmployeeChip_EmployeeQuery } from 'src/generated/mloop-graphql';

import OptionalAttendeeToggle from 'src/entities/common/OptionalAttendeeToggle';

import { SmartEmployeeSelectChipAction } from './chipAction';
import { chipGroupSx } from './chipLayout';
import { DynamicAudienceChipIconVariant, getDacIcon, getSmartOptionLabel } from './utils';

/**
 * Extracts the best available display name from an employee object.
 * Falls back through fullName → givenName → familyName → email.
 * @param employee Employee data with optional name fields
 * @returns Display name string or empty string if none available
 */
const getEmployeeName = (employee: {
  fullName?: string | null;
  givenName?: string | null;
  familyName?: string | null;
  email?: string | null;
}) => employee.fullName ?? employee.givenName ?? employee.familyName ?? employee.email ?? '';

export const SmartEmployeeSelectChipEmployeeFragment = gql`
  query SmartEmployeeSelectLabelEmployeeChip_employee($id: uuid!) {
    employee(id: $id) {
      id
      fullName
      givenName
      familyName
      email
      internalProfilePictureUrl
    }
  }
`;

type Props = {
  /** The employee ID to fetch and display */
  id: EmployeeId;
  /** Callback fired when the delete icon is clicked */
  onDelete?: () => void;
  /** Opt-in required/optional control rendered inside the chip. Only attendee surfaces pass one. */
  action?: SmartEmployeeSelectChipAction;
  /** Set when this person is on the invite because an audience resolved to them. */
  sourceAudience?: DynamicAudience;
};

/**
 * Renders a Chip with the employee's avatar and name, using Apollo for fetching the employee details.
 * Provides skeleton during loading and error display for fetch failure.
 */
const SmartEmployeeSelectChipEmployee: FC<Props> = ({ id, onDelete, action, sourceAudience }) => {
  const { t } = useTranslation();
  const { data, loading, error } = useSmartEmployeeSelectLabelEmployeeChip_EmployeeQuery({
    variables: { id },
  });

  if (loading) {
    return (
      <Skeleton variant="text" width={100} height={24} data-testid="smart-employee-select-chip-employee-loading" />
    );
  }

  if (error) {
    return (
      <Chip
        size="small"
        color="error"
        label={t('hiring-dashboard-common:common.error')}
        onDelete={onDelete}
        data-testid="smart-employee-select-chip-employee-error"
      />
    );
  }

  const employee = data?.employee;
  if (!employee) {
    return null;
  }

  // A member is shown as "<audience> (<name>)" -- the same convention the audience chip uses when it
  // resolves to one person. The audience has no chip of its own on the communications surfaces, so
  // this label is the only place the provenance is stated.
  const audienceLabel = sourceAudience ? getSmartOptionLabel(sourceAudience, t) : '';
  const label = audienceLabel ? `${audienceLabel} (${getEmployeeName(employee)})` : getEmployeeName(employee);

  const chip = (
    <Chip
      variant="filled"
      icon={
        sourceAudience ? (
          getDacIcon(sourceAudience, DynamicAudienceChipIconVariant.Primary)
        ) : (
          <Avatar
            src={employee.internalProfilePictureUrl ?? ''}
            alt={t('hiring-dashboard-common:common.userChipAlt')}
          />
        )
      }
      size="small"
      label={label}
      onDelete={onDelete}
      data-testid={
        sourceAudience ? 'smart-employee-select-chip-audience-member' : 'smart-employee-select-chip-employee'
      }
    />
  );

  // The toggle is a sibling of the chip, never inside it: a Chip with onDelete renders its own
  // interactive root, and a button inside a button is invalid markup that assistive technology and
  // strict click checks both trip over.
  return action ? (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={chipGroupSx}>
      {chip}
      <OptionalAttendeeToggle isAttendeeOptional={action.isOptional} onUserIconClick={action.onToggle} />
    </Stack>
  ) : (
    chip
  );
};

export default SmartEmployeeSelectChipEmployee;
